import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { HistoryContributionStatus } from '@gwc/shared';
import { historyApi } from '../api/historyContributions';
import { Pagination } from '../components/Pagination';
import { PageSkeleton } from '../components/Skeleton';
import { ContributionStatusBadge, STATUS_LABEL } from '../components/ContributionStatusBadge';

const TABS: HistoryContributionStatus[] = ['submitted', 'needs_info', 'merged', 'declined', 'draft'];

/** The Records Admin queue. */
export function ContributionReview() {
  const [status, setStatus] = useState<HistoryContributionStatus>('submitted');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['contributions', 'all', status, page],
    queryFn: () => historyApi.list({ scope: 'all', status, page }),
  });

  return (
    <div className="space-y-5">
      <div>
        <Link to="/archive" className="text-xs text-cream-300/60 hover:text-gold-200">
          ← Archive
        </Link>
        <h1 className="mt-2 font-display text-3xl text-cream-50">Review contributions</h1>
        <p className="mt-1 text-sm text-cream-300/70">What members have sent in about past tastings. Merge them into the archive, ask for more, or decline.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            className={`rounded-full border px-3 py-1 text-xs ${status === t ? 'border-gold-400 text-gold-200' : 'border-ink-800 text-cream-300/60 hover:text-cream-100'}`}
            onClick={() => {
              setStatus(t);
              setPage(1);
            }}
          >
            {STATUS_LABEL[t]}
          </button>
        ))}
      </div>
      {isLoading ? (
        <PageSkeleton lines={4} />
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-cream-300/60">Nothing here.</p>
      ) : (
        <ul className="divide-y divide-ink-800 rounded border border-ink-800">
          {data.items.map((c) => (
            <li key={c.id}>
              <Link to={`/contribute/review/${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-ink-900/40">
                <div>
                  <p className="text-cream-50">
                    {c.title ?? <span className="text-cream-300/50">Untitled</span>}
                    {c.year ? <span className="ml-2 text-cream-300/50">{c.year}</span> : null}
                    {c.linkedEventId && <span className="ml-2 text-xs text-gold-400">adds to an archived tasting</span>}
                  </p>
                  <p className="text-xs text-cream-300/50">
                    {c.submitterName} · {c.fileCount} file{c.fileCount === 1 ? '' : 's'} ·{' '}
                    {c.submittedAt ? `submitted ${new Date(c.submittedAt).toLocaleDateString()}` : `updated ${new Date(c.updatedAt).toLocaleDateString()}`}
                  </p>
                </div>
                <ContributionStatusBadge status={c.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </div>
  );
}
